import {
  AnnotationType,
  ArrowAnnotation,
  CircleAnnotation,
  HitTestParams,
  PenAnnotation,
  RectangleAnnotation,
  TextAnnotation,
} from '../types';

/**
 * Returns true when the pointer lies within `threshold` pixels (in un-rotated original image space)
 * of the annotation. Highlighter is given a thicker hit band.
 */
export const hitTest = ({ annotation, pointerPosition: p, threshold }: HitTestParams): boolean => {
  switch (annotation.type) {
    case AnnotationType.Pen:
    case AnnotationType.Highlighter: {
      const pen = annotation as PenAnnotation;
      const band = annotation.type === AnnotationType.Highlighter ? threshold * 2 : threshold;
      const { points } = pen;
      if (points.length === 0) return false;
      if (points.length === 1) {
        const only = points[0];
        return Math.abs(only.x - p.x) < band && Math.abs(only.y - p.y) < band;
      }
      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len2 = dx * dx + dy * dy;
        if (len2 === 0) {
          if (Math.abs(a.x - p.x) < band && Math.abs(a.y - p.y) < band) return true;
          continue;
        }
        let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
        t = Math.max(0, Math.min(1, t));
        const ox = a.x + t * dx;
        const oy = a.y + t * dy;
        if (Math.sqrt((p.x - ox) ** 2 + (p.y - oy) ** 2) < band) return true;
      }
      return false;
    }
    case AnnotationType.Arrow: {
      const ar = annotation as ArrowAnnotation;
      const dx = ar.end.x - ar.start.x;
      const dy = ar.end.y - ar.start.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) {
        return Math.abs(ar.start.x - p.x) < threshold && Math.abs(ar.start.y - p.y) < threshold;
      }
      let t = ((p.x - ar.start.x) * dx + (p.y - ar.start.y) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      const ox = ar.start.x + t * dx;
      const oy = ar.start.y + t * dy;
      return Math.sqrt((p.x - ox) ** 2 + (p.y - oy) ** 2) < threshold;
    }
    case AnnotationType.Rectangle: {
      const r = annotation as RectangleAnnotation;
      return (
        p.x >= r.origin.x - threshold &&
        p.x <= r.origin.x + r.width + threshold &&
        p.y >= r.origin.y - threshold &&
        p.y <= r.origin.y + r.height + threshold
      );
    }
    case AnnotationType.Circle: {
      const c = annotation as CircleAnnotation;
      if (c.radiusX < 1 || c.radiusY < 1) return false;
      const nx = (p.x - c.center.x) / c.radiusX;
      const ny = (p.y - c.center.y) / c.radiusY;
      const dist = Math.sqrt(nx * nx + ny * ny);
      return dist <= 1 + threshold / Math.min(c.radiusX, c.radiusY);
    }
    case AnnotationType.Text: {
      const t = annotation as TextAnnotation;
      const textWidth = Math.max(t.text.length * t.fontSize * 0.6, 30);
      const textHeight = t.fontSize * 1.4;
      return (
        p.x >= t.position.x - threshold &&
        p.x <= t.position.x + textWidth + threshold &&
        p.y >= t.position.y - textHeight - threshold &&
        p.y <= t.position.y + threshold
      );
    }
    default:
      return false;
  }
};
